import axios from "axios";
import Links from "../constants/Links";

/**
 * Reviewer API service for managing PG reviewers
 * Reviewers are existing lecturers assigned the REVIEWER role
 * HOD operates within their department; DEAN_SPGS operates institution-wide
 */
const Reviewers = {
  /**
   * Assign reviewer role to an existing lecturer
   * @param {number} lecturerId - Lecturer ID
   * @returns {Promise<object>}
   */
  assignRole: async (lecturerId) => {
    return axios.post(Links.reviewers.assignRole + lecturerId);
  },

  /**
   * Remove reviewer role from a lecturer
   * @param {number} lecturerId - Lecturer ID
   * @returns {Promise<void>}
   */
  removeRole: async (lecturerId) => {
    return axios.delete(Links.reviewers.removeRole + lecturerId);
  },

  /**
   * Get reviewers in caller's scope
   * HOD → reviewers in HOD's department
   * DEAN_SPGS → reviewers across all departments
   * @returns {Promise<object[]>}
   */
  getForDepartment: async () => {
    return axios.get(Links.reviewers.getForDepartment);
  },

  /**
   * Get reviewers for a specific department
   * @param {number} departmentId - Department ID
   * @returns {Promise<object[]>}
   */
  getForSpecificDepartment: async (departmentId) => {
    return axios.get(Links.reviewers.getForSpecificDepartment + departmentId);
  },

  /**
   * Get eligible lecturers who can become reviewers
   * Lecturers in caller's scope without the REVIEWER role
   * @returns {Promise<object[]>}
   */
  getEligibleLecturers: async () => {
    return axios.get(Links.reviewers.getEligibleLecturers);
  },

  /**
   * Assign reviewer to a student
   * @param {string} studentId - Student user ID
   * @param {number} lecturerId - Reviewer lecturer ID
   * @returns {Promise<object>}
   */
  assignToStudent: async (studentId, lecturerId) => {
    return axios.post(Links.reviewers.assignToStudent, null, {
      params: {
        studentId,
        lecturerId
      }
    });
  },

  /**
   * Remove reviewer from a student
   * @param {string} studentId - Student user ID
   * @returns {Promise<object>}
   */
  removeFromStudent: async (studentId) => {
    return axios.delete(Links.reviewers.removeFromStudent, {
      params: {
        studentId
      }
    });
  },

  /**
   * Get students assigned to a specific reviewer
   * @param {number} lecturerId - Reviewer lecturer ID
   * @returns {Promise<object[]>}
   */
  getStudentsForReviewer: async (lecturerId) => {
    return axios.get(Links.reviewers.getStudentsForReviewer + lecturerId);
  },

  /**
   * Get students assigned to the current reviewer (self)
   * @returns {Promise<object[]>}
   */
  getMyStudents: async () => {
    return axios.get(Links.reviewers.getMyStudents);
  }
};

export default Reviewers;
